import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Mail, MapPin, Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { contactInfo } from '../data/mock';

const ContactHero = () => (
  <section className="pt-32 pb-20 bg-[#1a1a1a]">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="max-w-3xl">
        <p className="text-white/40 text-sm tracking-[0.2em] uppercase mb-4">
          Contact
        </p>
        <h1 
          className="text-4xl lg:text-6xl text-white mb-6 leading-tight"
          style={{ fontFamily: 'Playfair Display, serif' }}
          data-testid="contact-title"
        >
          Parlons de <span className="italic">votre projet</span>
        </h1>
        <p className="text-white/60 text-xl leading-relaxed">
          Notre équipe vous accompagne dans votre recherche 
          de la résidence idéale aux Terres de Songon.
        </p>
      </div>
    </div>
  </section>
);

const ContactInfoSidebar = () => (
  <div>
    <h2 
      className="text-2xl text-[#1a1a1a] mb-8"
      style={{ fontFamily: 'Playfair Display, serif' }}
    >
      Nos coordonnées
    </h2>
    
    <div className="space-y-6">
      <div className="flex gap-4">
        <div className="w-12 h-12 bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
          <Phone className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-[#666] text-sm mb-1">Téléphone</p>
          <a href={`tel:${contactInfo.phone}`} className="text-[#1a1a1a] font-medium hover:text-[#2D5A27] transition-colors">
            {contactInfo.phone}
          </a>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="w-12 h-12 bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-[#666] text-sm mb-1">Email</p>
          <a href={`mailto:${contactInfo.email}`} className="text-[#1a1a1a] font-medium hover:text-[#2D5A27] transition-colors">
            {contactInfo.email}
          </a>
        </div>
      </div>

      <div className="flex gap-4">
        <div className="w-12 h-12 bg-[#1a1a1a] flex items-center justify-center flex-shrink-0">
          <MapPin className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="text-[#666] text-sm mb-1">Adresse</p>
          <p className="text-[#1a1a1a] font-medium">
            {contactInfo.address}
          </p>
        </div>
      </div>
    </div>

    <div className="mt-12 p-6 bg-[#F5F5F5]">
      <p className="text-[#666] text-sm">
        Réponse garantie sous 24h. Notre équipe est à votre disposition 
        du lundi au samedi de 9h à 18h.
      </p>
    </div>
  </div>
);

const StatusMessage = ({ type, message }) => {
  if (type === 'success') {
    return (
      <div className="mb-8 p-6 bg-[#2D5A27]/10 border border-[#2D5A27]/20 rounded-lg flex items-start gap-4" data-testid="form-success">
        <CheckCircle className="w-6 h-6 text-[#2D5A27] flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-lg font-medium text-[#2D5A27] mb-1">Message envoyé avec succès !</h3>
          <p className="text-[#2D5A27]/80">{message}</p>
        </div>
      </div>
    );
  }
  if (type === 'error') {
    return (
      <div className="mb-8 p-6 bg-red-50 border border-red-200 rounded-lg flex items-start gap-4" data-testid="form-error">
        <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="text-lg font-medium text-red-900 mb-1">Erreur d'envoi</h3>
          <p className="text-red-700">{message}</p>
        </div>
      </div>
    );
  }
  return null;
};

const interests = [
  "Villas de Luxe",
  "Duplex",
  "Appartements Haut de Gamme",
  "Terrain",
  "Visite sur place",
  "Renseignements généraux"
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    interest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: null, message: '' });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${apiUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitStatus({
          type: 'success',
          message: data.message || 'Merci pour votre message ! Nous vous recontacterons très bientôt.'
        });
        setFormData({ name: '', email: '', phone: '', interest: '', message: '' });
      } else {
        setSubmitStatus({
          type: 'error',
          message: data.detail || 'Une erreur s\'est produite. Veuillez réessayer.'
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus({
        type: 'error',
        message: 'Impossible d\'envoyer le message. Vérifiez votre connexion internet.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="contact-page">
      <Header />
      <main>
        <ContactHero />

        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-3 gap-16">
              <ContactInfoSidebar />

              <div className="lg:col-span-2">
                <div className="bg-white p-10 card-shadow">
                  <h2 
                    className="text-2xl text-[#1a1a1a] mb-8"
                    style={{ fontFamily: 'Playfair Display, serif' }}
                  >
                    Envoyez-nous un message
                  </h2>

                  <StatusMessage type={submitStatus.type} message={submitStatus.message} />

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[#666] text-sm mb-2">Nom complet *</label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          minLength={2}
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 bg-[#F5F5F5] border-0 focus:ring-2 focus:ring-[#1a1a1a] transition-all text-[#1a1a1a] disabled:opacity-50"
                          placeholder="Votre nom"
                          data-testid="input-name"
                        />
                      </div>
                      <div>
                        <label className="block text-[#666] text-sm mb-2">Email *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 bg-[#F5F5F5] border-0 focus:ring-2 focus:ring-[#1a1a1a] transition-all text-[#1a1a1a] disabled:opacity-50"
                          placeholder="votre@email.com"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-[#666] text-sm mb-2">Téléphone *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          minLength={8}
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 bg-[#F5F5F5] border-0 focus:ring-2 focus:ring-[#1a1a1a] transition-all text-[#1a1a1a] disabled:opacity-50"
                          placeholder="+225 00 00 00 00"
                          data-testid="input-phone"
                        />
                      </div>
                      <div>
                        <label className="block text-[#666] text-sm mb-2">Intéressé par *</label>
                        <select
                          name="interest"
                          value={formData.interest}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          className="w-full px-4 py-3 bg-[#F5F5F5] border-0 focus:ring-2 focus:ring-[#1a1a1a] transition-all text-[#1a1a1a] disabled:opacity-50"
                          data-testid="input-interest"
                        >
                          <option value="">Sélectionnez une option</option>
                          {interests.map((interest) => (
                            <option key={interest} value={interest}>{interest}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-[#666] text-sm mb-2">Message *</label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        minLength={10}
                        rows={5}
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 bg-[#F5F5F5] border-0 focus:ring-2 focus:ring-[#1a1a1a] transition-all resize-none text-[#1a1a1a] disabled:opacity-50"
                        placeholder="Décrivez votre projet ou votre demande..."
                        data-testid="input-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[#1a1a1a] text-white py-4 text-sm font-medium tracking-wide hover:bg-[#333] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Envoi en cours...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          Envoyer le message
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
